import type { ConnectError } from '@connectrpc/connect'
import {
  WorkflowCaseErrorReason,
  WorkflowCaseErrorReasonSchema,
} from '@ebalance/proto/bffuser/v1/workflow_case_model_pb'
import type { TFunction } from 'i18next'

import {
  type AppErrorInfoReasonEntry,
  resolveAppErrorInfoMessage,
} from '~/libs/appErrorInfo'

/**
 * `WorkflowCaseErrorReason` (backend `workflowcase` domain) → i18n key
 * registry for 一括作成ダイアログ (`CreateWorkflowCasesDialog`, issue #5219
 * Decisions #13/#15)。
 *
 * `TEMPLATE_REQUIRES_BG` is operation-scoped (単一入力欄に帰属しない。原因は
 * 「雛形の選択」だが、BG 不要モードでは直せる入力欄が無い)。
 * `APPROVER_ALL_INACTIVE` / `WORKFLOW_CASE_DELETED` はこのダイアログの操作
 * (一括作成) の error 経路では発生しない reason なので、専用文言を持たせず
 * fallback に委ねる。
 */
const CREATE_DIALOG_REASON_KEYS: Record<
  WorkflowCaseErrorReason,
  AppErrorInfoReasonEntry | undefined
> = {
  [WorkflowCaseErrorReason.UNSPECIFIED]: undefined,
  [WorkflowCaseErrorReason.APPROVER_ALL_INACTIVE]: undefined,
  [WorkflowCaseErrorReason.WORKFLOW_CASE_DELETED]: undefined,
  [WorkflowCaseErrorReason.TEMPLATE_REQUIRES_BG]: {
    key: 'page:workflowCases.serverError.templateRequiresBg',
  },
  [WorkflowCaseErrorReason.MANUAL_COMPLETION_AWAITING_APPROVAL]: undefined,
  // SUSPENDED は一括作成では発生しない reason なので fallback に委ねる。
  [WorkflowCaseErrorReason.SUSPENDED]: undefined,
}

/**
 * 一括作成 (`CreateWorkflowCasesDialog`) の失敗 error から、issue #5219
 * Decisions #13 のガード拒否 (`TEMPLATE_REQUIRES_BG`) の専用メッセージを
 * 解決する。`google.rpc.ErrorInfo` detail が無い / reason が未知 /
 * registry entry が `undefined` の場合は `undefined` を返し、呼び出し元の
 * gRPC-code ベース fallback (`invalidArgument`) に委ねる。
 */
export function resolveTemplateRequiresBgMessage(
  err: ConnectError,
  t: TFunction,
): string | undefined {
  return resolveAppErrorInfoMessage(
    err,
    WorkflowCaseErrorReasonSchema,
    CREATE_DIALOG_REASON_KEYS,
    t,
  )?.message
}
